/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";
import { sanitizeUrl } from "@braintree/sanitize-url";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const VideoCtor = (typeof globalThis !== "undefined" && (globalThis.VideoNode || globalThis.Video)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const VIDEO_PROPS = [
  "src",
  "controls",
  "controlsVolume",
  "controlsFullscreen", 
  "controlsPlayPause",
  "controlsSeek",
  "controlsTime",
  "autoPlay",
  "loop",
  "projection",
  "enableOnProximity",
  "proximityRadius",
  "isInvisibleBackground",
  "openAs2D", 
  "audioOnlyInsideScene",
  "billboard",
  "href",
  "linkedModel",
  "disposeAnimation",
  "networkedId",
  "networkable"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class VideoEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    VIDEO_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  play() {
    const d = this._originalDescriptors.get("play");
    if (d?.value) d.value.call(this.sourceObject);
    else if (this.sourceObject?.play) this.sourceObject.play();
  }

  pause() {
    const d = this._originalDescriptors.get("pause");
    if (d?.value) d.value.call(this.sourceObject);
    else if (this.sourceObject?.pause) this.sourceObject.pause();
  }

  stop() {
    const d = this._originalDescriptors.get("stop");
    if (d?.value) d.value.call(this.sourceObject);
    else if (this.sourceObject?.stop) this.sourceObject.stop();
  }

  get paused() {
    const d = this._originalDescriptors.get("paused");
    if (d?.get) return d.get.call(this.sourceObject);
    return this.sourceObject?.paused ?? true;
  }

  dispose() {
    this.pause();
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class VideoAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const MEDIA_LOADER_COMPONENT = "media-loader";
    const BILLBOARD_COMPONENT = "billboard";
    const LINK_SEQ_COMPONENT = "link-sequence";
    const NETWORKED_COMPONENT = "networked";

    // We sometimes need to update the media-loader attribute before A-Frame
    // has fully initialised the component. In that situation `el.getAttribute`
    // returns `null`, so blindly writing would wipe the original config. We
    // therefore cache pending updates and flush them once the component is ready.

    const _pendingUpdates = {};

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT) || {})
      };

      // Top-level props ------------------------------------------------
      Object.keys(_pendingUpdates).forEach(key => {
        if (key === "mediaOptions") return; // handled separately
        current[key] = _pendingUpdates[key];
      });

      // Nested mediaOptions props -------------------------------------
      if (_pendingUpdates.mediaOptions) {
        current.mediaOptions = {
          ...(current.mediaOptions || {}),
          ..._pendingUpdates.mediaOptions
        };
      }

      // Clear cache and apply.
      Object.keys(_pendingUpdates).forEach(k => delete _pendingUpdates[k]);
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    // When the media-loader component becomes available, flush.
    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === MEDIA_LOADER_COMPONENT) {
          _flushPending();
        }
      });
    }

    const setMediaLoaderProp = (key, val, inMediaOptions = false) => {
      if (!this.sourceObject?.el) return;

      const attr = this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT);

      // If the component data is not yet present, cache the update.
      if (!attr) {
        if (inMediaOptions) {
          _pendingUpdates.mediaOptions = {
            ...(_pendingUpdates.mediaOptions || {}),
            [key]: val
          };
        } else {
          _pendingUpdates[key] = val;
        }
        return;
      }

      const current = { ...attr };
      if (inMediaOptions) {
        current.mediaOptions = { ...(current.mediaOptions || {}), [key]: val };
      } else {
        current[key] = val;
      }
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    const getMediaLoaderProp = (key, inMediaOptions = false) => {
      const attr = this.sourceObject?.el?.getAttribute(MEDIA_LOADER_COMPONENT);
      if (attr) {
        return inMediaOptions ? attr.mediaOptions?.[key] : attr[key];
      }

      // Fall back to pending updates when component isn't initialized
      if (inMediaOptions) {
        return _pendingUpdates.mediaOptions?.[key];
      } else {
        return _pendingUpdates[key];
      }
    };

    // Sync video-pause-state -------------------------------------------
    const updateVideoPauseState = () => {
      const autoPlay = getMediaLoaderProp("autoPlay", true);
      const audioOnlyInsideScene = getMediaLoaderProp(
        "audioOnlyInsideScene",
        true
      );
      const paused = !autoPlay || audioOnlyInsideScene;
      this.sourceObject?.el?.setAttribute("video-pause-state", { paused });
    };

    // src (top-level) ---------------------------------------------------
    Object.defineProperty(this, "src", {
      get: () => getMediaLoaderProp("src"),
      set: v => {
        setMediaLoaderProp("src", v);
      }
    });

    // mediaOptions properties ------------------------------------------
    [
      "projection",
      "openAs2D",
      "autoPlay",
      "loop",
      "isInvisibleBackground",
      "audioOnlyInsideScene"
    ].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getMediaLoaderProp(prop, true),
        set: v => {
          setMediaLoaderProp(prop, v, true);
          if (prop === "autoPlay" || prop === "audioOnlyInsideScene") {
            updateVideoPauseState();
          }
        }
      });
    });

    // Proximity helpers & networked updates (mirrors AudioElement)
    const getEnableOnProximityAttr = () =>
      this.sourceObject?.el?.getAttribute("enable-on-proximity");

    const updateNetworkedComponent = () => {
      if (!this.sourceObject?.el) return;
      const el = this.sourceObject.el;

      const enableProximity = el.hasAttribute("enable-on-proximity");

      if (!enableProximity) {
        if (!el.hasAttribute("networked")) {
          const src = getMediaLoaderProp("src");
          const isControlled = this.controls !== false && !this.enableOnProximity;
          el.setAttribute("networked", {
            template: isControlled
              ? "#static-controlled-media"
              : "#static-media"
          });
        }
      } else if (el.hasAttribute("networked")) {
        el.removeAttribute("networked");
      }
    };

    // enableOnProximity --------------------------------------------------
    Object.defineProperty(this, "enableOnProximity", {
      get: () =>
        this.sourceObject?.el?.hasAttribute("enable-on-proximity") || false,
      set: v => {
        if (!this.sourceObject?.el) return;

        if (v) {
          const current = getEnableOnProximityAttr() || {};
          const distance = current.distance ?? this.proximityRadius ?? 1;
          this.sourceObject.el.setAttribute("enable-on-proximity", {
            distance
          });
        } else {
          this.sourceObject.el.removeAttribute("enable-on-proximity");
        }

        updateNetworkedComponent();
      }
    });

    // proximityRadius ----------------------------------------------------
    Object.defineProperty(this, "proximityRadius", {
      get: () => getEnableOnProximityAttr()?.distance,
      set: v => {
        if (!this.sourceObject?.el) return;
        if (this.sourceObject.el.hasAttribute("enable-on-proximity")) {
          const current = getEnableOnProximityAttr() || {};
          this.sourceObject.el.setAttribute("enable-on-proximity", {
            ...current,
            distance: v
          });
        }
      }
    });

    // controls maps to hidePlaybackControls ----------------------------
    Object.defineProperty(this, "controls", {
      get: () => getMediaLoaderProp("hidePlaybackControls", true) === false,
      set: v => {
        setMediaLoaderProp("hidePlaybackControls", !v, true);
      }
    });

    // granular controls properties -------------------------------------
    Object.defineProperty(this, "controlsVolume", {
      get: () => getMediaLoaderProp("controlsVolume", true),
      set: v => {
        setMediaLoaderProp("controlsVolume", v, true);
      }
    });

    Object.defineProperty(this, "controlsFullscreen", {
      get: () => getMediaLoaderProp("controlsFullscreen", true),
      set: v => {
        setMediaLoaderProp("controlsFullscreen", v, true);
      }
    });

    Object.defineProperty(this, "controlsPlayPause", {
      get: () => getMediaLoaderProp("controlsPlayPause", true),
      set: v => {
        setMediaLoaderProp("controlsPlayPause", v, true);
      }
    });

    Object.defineProperty(this, "controlsSeek", {
      get: () => getMediaLoaderProp("controlsSeek", true),
      set: v => {
        setMediaLoaderProp("controlsSeek", v, true);
      }
    });

    Object.defineProperty(this, "controlsTime", {
      get: () => getMediaLoaderProp("controlsTime", true),
      set: v => {
        setMediaLoaderProp("controlsTime", v, true);
      }
    });

    // billboard boolean attribute --------------------------------------
    Object.defineProperty(this, "billboard", {
      get: () => this.sourceObject?.el?.hasAttribute(BILLBOARD_COMPONENT),
      set: v => {
        if (!this.sourceObject?.el) return;
        if (v) {
          this.sourceObject.el.setAttribute(BILLBOARD_COMPONENT, "");
        } else {
          this.sourceObject.el.removeAttribute(BILLBOARD_COMPONENT);
        }
      }
    });

    // link-like props stored in mediaOptions ---------------------------
    Object.defineProperty(this, "href", {
      get: () => getMediaLoaderProp("href", true),
      set: v => {
        setMediaLoaderProp("href", v, true);
      }
    });

    // link-sequence component props ------------------------------------
    ["linkedModel", "disposeAnimation"].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () =>
          this.sourceObject?.el?.getAttribute(LINK_SEQ_COMPONENT)?.[prop],
        set: v => {
          if (!this.sourceObject?.el) return;
          const current = {
            ...(this.sourceObject.el.getAttribute(LINK_SEQ_COMPONENT) || {})
          };
          current[prop] = v;
          this.sourceObject.el.setAttribute(LINK_SEQ_COMPONENT, current);
        }
      });
    });

    // networked id ------------------------------------------------------
    Object.defineProperty(this, "networkedId", {
      get: () => this.sourceObject?.el?.getAttribute(NETWORKED_COMPONENT)?.id,
      set: v => {
        if (!this.sourceObject?.el) return;
        if (v === undefined || v === null || v === "") {
          this.sourceObject.el.removeAttribute(NETWORKED_COMPONENT);
        } else {
          this.sourceObject.el.setAttribute(NETWORKED_COMPONENT, { id: v });
        }
      }
    });

    // networkable boolean --------------------------------------------------
    Object.defineProperty(this, "networkable", {
      get: () => this.sourceObject?.el?.hasAttribute(NETWORKED_COMPONENT),
      set: v => {
        if (!this.sourceObject?.el) return;
        if (v) {
          if (!this.sourceObject.el.hasAttribute(NETWORKED_COMPONENT)) {
            const src = getMediaLoaderProp("src");
            const isControlled =
              this.controls !== false && !this.enableOnProximity;
            this.sourceObject.el.setAttribute(NETWORKED_COMPONENT, {
              template: isControlled
                ? "#static-controlled-media"
                : "#static-media"
            });
          }
        } else {
          this.sourceObject.el.removeAttribute(NETWORKED_COMPONENT);
        }
      }
    });

    Object.defineProperty(this, "paused", {
      get: () => {
        if (!this.sourceObject?.el) return true;
        const loader = this.sourceObject.el.components?.["media-loader"];
        return loader?.videoPaused ?? true;
      }
    });

    this.play = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) mv.playVideo?.();
      else setMediaLoaderProp("videoPaused", false, true);
    };

    this.pause = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) {
        if (mv.pauseVideo) mv.pauseVideo();
        else el.setAttribute("media-video", "videoPaused", true);
      } else {
        setMediaLoaderProp("videoPaused", true, true);
      }
    };

    this.stop = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) {
        if (mv.stopVideo) mv.stopVideo();
        else mv.stop?.();
      } else {
        setMediaLoaderProp("videoPaused", true, true);
        setMediaLoaderProp("time", 0);
      }
    };
  }

  pause() {
    this.sourceObject?.el?.querySelectorAll("video").forEach(v => v.pause?.());
  }

  dispose() {
    this.pause();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  const interested = [...VIDEO_PROPS, "play", "pause", "paused", "stop"];
  for (const prop of interested) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new VideoAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new VideoEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class VideoElement extends ScriptableObject {
  static scriptableType = "video";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      
      const isControlled = initial.controls !== false && !initial.enableOnProximity;
      const templateName = isControlled ? "#static-controlled-media" : "#static-media";

      if (initial.networkable) {
        el.setAttribute("networked", {
          template: templateName
        });
      }

      const initialAutoPlay = initial.autoPlay ?? true;
      const videoPaused = !initialAutoPlay || initial.audioOnlyInsideScene;
      el.setAttribute("video-pause-state", { paused: videoPaused });

      const mediaOptions = {
        projection: initial.projection,
        openAs2D: initial.openAs2D,
        autoPlay: initialAutoPlay,
        loop: initial.loop,
        isInvisibleBackground: initial.isInvisibleBackground,
        audioOnlyInsideScene: initial.audioOnlyInsideScene,
        hidePlaybackControls: !isControlled,
        controlsVolume: initial.controlsVolume !== undefined ? initial.controlsVolume : isControlled,
        controlsFullscreen: initial.controlsFullscreen !== undefined ? initial.controlsFullscreen : isControlled,
        controlsPlayPause: initial.controlsPlayPause !== undefined ? initial.controlsPlayPause : isControlled,
        controlsSeek: initial.controlsSeek !== undefined ? initial.controlsSeek : isControlled,
        controlsTime: initial.controlsTime !== undefined ? initial.controlsTime : isControlled,
        href: initial.href
      };

      el.setAttribute("media-loader", {
        src: sanitizeUrl(initial.src || ""),
        fitToBox: false,
        resolve: true,
        fileIsOwned: true,
        animate: false,
        mediaOptions,
        moveTheParentNotTheMesh: true,
        useFancyLoader: false
      });

      if (initial.billboard) {
        el.setAttribute("billboard", "");
      }

      if (initial.enableOnProximity) {
        el.setAttribute("enable-on-proximity", {
          distance: initial.proximityRadius
        });
      }

      if (initial.linkedModel || initial.disposeAnimation) {
        el.setAttribute("link-sequence", {
          linkedModel: initial.linkedModel,
          disposeAnimation: initial.disposeAnimation
        });
      }

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real VideoNode and upgrade to scriptable
    const node = createVideoElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public video props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of VIDEO_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of VIDEO_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new VideoElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  play() {
    return ensureAdapter(this)?.play?.();
  }

  pause() {
    return ensureAdapter(this)?.pause?.();
  }

  stop() {
    return ensureAdapter(this)?.stop?.();
  }

  get paused() {
    return ensureAdapter(this)?.paused ?? true;
  }

  get playing() {
    return !this.paused;
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor VideoNode instance to Scriptable, attach adapter and proxies
function makeEditorVideoNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || VideoCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: VideoElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = VideoElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new VideoEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new VideoEditorAdapter(node, descriptors);
  }

  for (const prop of VIDEO_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  try {
    if (node.clone !== VideoElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: VideoElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }

    ["play", "pause", "stop"].forEach(m => {
      Object.defineProperty(node, m, {
        value: VideoElement.prototype[m],
        writable: true,
        configurable: true
      });
    });

    ["paused", "playing"].forEach(p => {
      const d = Object.getOwnPropertyDescriptor(VideoElement.prototype, p);
      if (d) Object.defineProperty(node, p, d);
    });
  } catch (e) {
    console.error("VideoElement: failed to patch", e);
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
VIDEO_PROPS.forEach(prop => {
  Object.defineProperty(VideoElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createVideoElementEditor(initial = {}) {
  const VideoCtor = (typeof globalThis !== "undefined" && (globalThis.VideoNode || globalThis.Video)) || undefined;
  
  if (typeof VideoCtor !== "function") {
    throw new Error(
      "VideoElementEditor: VideoNode constructor not found – cannot create element in editor runtime."
    );
  }

  const editor = ScriptableObject.editor;
  if (!editor) {
    throw new Error(
      "VideoElementEditor: ScriptableObject.editor not available – cannot create element in editor runtime."
    );
  }

  if (!editor.audioListener) {
    throw new Error(
      "VideoElementEditor: ScriptableObject.editor.audioListener not available – cannot create element in editor runtime."
    );
  }

  // Check if AudioElementType is available
  const AudioElementType = globalThis.AudioElementType;
  if (!AudioElementType) {
    throw new Error(
      "VideoElementEditor: AudioElementType not available – cannot create element in editor runtime."
    );
  }

  let node;
  try {
    node = new VideoCtor(editor);
  } catch (e) {
    // If VideoNode constructor fails, provide more detailed error information
    const errorMessage = e?.message || e;
    if (
      errorMessage.includes(
        "Super expression must either be null or a function"
      )
    ) {
      throw new Error(
        "VideoElementEditor: VideoNode constructor failed due to missing dependencies (AudioParamsNode or Video class). This usually means the scripting runtime environment is not properly set up."
      );
    }
    throw new Error(
      `VideoElementEditor: failed to construct VideoNode – ${errorMessage}`
    );
  }

  makeEditorVideoNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("video", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== VideoElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return VideoElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorVideoNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { VideoAFrameAdapter, VideoEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(VideoElement.prototype, Symbol.toStringTag, { value: "VideoElement" });